import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaExternalLinkAlt, FaSyncAlt } from 'react-icons/fa';
import { usePasStore } from '../pasStore';
import type { Policy } from '../types';
import { daysBetween, formatCurrency, formatDate, todayStr } from '../utils';
import { Button } from '../components/Button';
import { DataTable, type Column } from '../components/DataTable';
import { RenewPolicyModal } from '../components/RenewPolicyModal';
import { PageHeader, SectionCard, StatCard } from '../components/primitives';
import { PolicyStatusBadge } from '../components/StatusBadges';

export const RenewalsPage: React.FC = () => {
  const navigate = useNavigate();
  const policies = usePasStore((s) => s.policies);
  const loading = usePasStore((s) => s.loading);
  const [renewTarget, setRenewTarget] = useState<Policy | null>(null);

  const upcoming = useMemo(
    () =>
      policies
        .filter((p) => p.status === 'active' || p.status === 'issued')
        .sort((a, b) => a.expirationDate.localeCompare(b.expirationDate)),
    [policies],
  );

  const renewed = useMemo(() => policies.filter((p) => p.status === 'renewed'), [policies]);

  const dueSoon = upcoming.filter((p) => daysBetween(todayStr(), p.expirationDate) <= 60).length;
  const renewalPremium = upcoming.reduce((sum, p) => sum + p.annualPremium, 0);

  const columns: Column<Policy>[] = [
    {
      key: 'policyNumber',
      header: 'Policy',
      primary: true,
      sortValue: (p) => p.policyNumber,
      accessor: (p) => (
        <div>
          <span>{p.policyNumber}</span>
          <span className="block text-xs font-normal" style={{ color: 'var(--color-text-secondary)' }}>{p.insured.name}</span>
        </div>
      ),
    },
    { key: 'status', header: 'Status', sortValue: (p) => p.status, accessor: (p) => <PolicyStatusBadge status={p.status} /> },
    { key: 'expires', header: 'Expires', sortValue: (p) => p.expirationDate, accessor: (p) => <span className="text-sm">{formatDate(p.expirationDate)}</span> },
    {
      key: 'days',
      header: 'Days left',
      align: 'right',
      sortValue: (p) => daysBetween(todayStr(), p.expirationDate),
      accessor: (p) => {
        const days = daysBetween(todayStr(), p.expirationDate);
        return <span className="text-sm font-semibold" style={{ color: days <= 30 ? 'var(--color-error-600)' : days <= 60 ? 'var(--color-warning-600)' : 'var(--color-text-secondary)' }}>{days}d</span>;
      },
    },
    { key: 'premium', header: 'Premium', align: 'right', sortValue: (p) => p.annualPremium, accessor: (p) => <span className="text-sm font-semibold">{formatCurrency(p.annualPremium)}</span> },
    {
      key: 'action',
      header: '',
      align: 'right',
      accessor: (p) => (
        <Button size="sm" variant="secondary" leftIcon={<FaSyncAlt className="h-3 w-3" />} onClick={() => setRenewTarget(p)}>
          Renew
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Renewals" subtitle="Manage the renewal pipeline and continue coverage for expiring policies." />

      <div className="mb-6 grid grid-cols-2 gap-4 xl:grid-cols-3">
        <StatCard label="Renewal pipeline" value={upcoming.length} icon={<FaSyncAlt />} accent="info" />
        <StatCard label="Due within 60 days" value={dueSoon} icon={<FaSyncAlt />} accent="warning" />
        <StatCard label="Pipeline premium" value={formatCurrency(renewalPremium)} icon={<FaSyncAlt />} accent="primary" />
      </div>

      <div className="mb-6">
        <DataTable
          columns={columns}
          data={upcoming}
          getRowId={(p) => p.id}
          onRowClick={(p) => navigate(`/policy-admin/policies/${p.id}`)}
          loading={loading}
          searchable
          searchPlaceholder="Search renewal pipeline…"
          searchText={(p) => `${p.policyNumber} ${p.insured.name}`}
          initialSort={{ key: 'days', direction: 'asc' }}
          emptyIcon={<FaSyncAlt />}
          emptyTitle="No policies in the renewal pipeline"
        />
      </div>

      {renewed.length > 0 && (
        <SectionCard title="Recently renewed" description="Policies that have completed a renewal" bodyClassName="p-0">
          <ul>
            {renewed.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 border-b px-5 py-3.5 last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{p.insured.name}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{p.policyNumber} · expired {formatDate(p.expirationDate)}</p>
                </div>
                {p.renewedToId && (
                  <Button size="sm" variant="ghost" rightIcon={<FaExternalLinkAlt className="h-3 w-3" />} onClick={() => navigate(`/policy-admin/policies/${p.renewedToId}`)}>
                    View renewal
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {renewTarget && <RenewPolicyModal isOpen={!!renewTarget} onClose={() => setRenewTarget(null)} policy={renewTarget} />}
    </div>
  );
};
